import React, { useState, useEffect } from 'react';
import { Mail, Plus, Edit2, Trash2, Send, TestTube, AlertCircle, CheckCircle, Users } from 'lucide-react';
import Navigation from '../components/Navigation/Navigation';
import SimpleEmailEditor from '../components/emailTemplates/SimpleEmailEditor';
import FileConflictModal from '../components/emailTemplates/FileConflictModal';
import BatchEmailModal from '../components/emailTemplates/BatchEmailModal';
import { useConfirmModal } from '../components/common/ConfirmModal';
import apiClient from '../utils/apiClient';
import './EmailTemplatesPage.css';

interface EmailTemplate {
  template_id: number;
  template_name: string;
  recipient_type: string;
  subject_template: string;
  body_template: string;
  weeks_ahead: number;
  include_song_list: boolean;
  include_metadata: boolean;
  include_video_links: boolean;
  music_folder_paths: string[];
  recipient_emails: any | null;
  compile_pdf: boolean;
  include_service_music: boolean;
  created_at: string;
  updated_at: string;
}

interface ConflictData {
  conflicts: any[];
  resolved: any[];
  message: string;
}

const EmailTemplatesPage: React.FC = () => {
  const { confirm, ConfirmModal: ConfirmDeleteModal } = useConfirmModal();
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Modal states
  const [showEditor, setShowEditor] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [showBatchModal, setShowBatchModal] = useState(false);
  
  // Selected template and conflict data
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [conflictData, setConflictData] = useState<ConflictData | null>(null);
  const [originalSendData, setOriginalSendData] = useState<any>(null);
  
  // Email connection status
  const [emailConnectionStatus, setEmailConnectionStatus] = useState<'unknown' | 'connected' | 'disconnected'>('unknown');
  const [emailConfig, setEmailConfig] = useState<any>(null);

  const fetchEmailConfig = async () => {
    try {
      const response = await apiClient.get('/music-email/config');
      setEmailConfig(response.data.config);
    } catch (error) {
      console.error('Failed to fetch email config:', error);
    }
  };

  useEffect(() => {
    fetchTemplates();
    testEmailConnection();
    fetchEmailConfig();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await apiClient.get('/email-templates');
      // Handle both formats: {success: true, data: [...]} or direct array
      const data = response.data.success ? response.data.data : response.data;
      setTemplates(data);
    } catch (error: any) {
      if (error.response?.status === 404) {
        // API endpoint doesn't exist - use mock data for development
        setTemplates([]);
        setError('Email templates database not set up. Please run the SQL setup script.');
      } else if (error.code === 'ECONNREFUSED' || error.message.includes('Network Error')) {
        setError('Backend server is not running. Please start the backend server.');
      } else {
        setError(error.response?.data?.message || error.message || 'Failed to fetch templates');
      }
    } finally {
      setLoading(false);
    }
  };

  const testEmailConnection = async () => {
    try {
      await apiClient.get('/music-email/test-connection');
      setEmailConnectionStatus('connected');
    } catch (error) {
      setEmailConnectionStatus('disconnected');
    }
  };

  const handleDeleteTemplate = (templateId: number) => {
    confirm({
      title: 'Delete Template',
      message: 'Are you sure you want to delete this email template?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await apiClient.delete(`/email-templates/${templateId}`);

          setTemplates(templates.filter(t => t.template_id !== templateId));
          setSuccessMessage('Email template deleted successfully');
          setTimeout(() => setSuccessMessage(null), 3000);
        } catch (error: any) {
          setError(error.response?.data?.message || error.message || 'Failed to delete template');
        }
      }
    });
  };

  const handleSaveTemplate = async (templateData: any) => {
    try {
      const isEditing = editingTemplate !== null;
      
      if (isEditing) {
        await apiClient.put(`/email-templates/${editingTemplate.template_id}`, templateData);
      } else {
        await apiClient.post('/email-templates', templateData);
      }
      
      await fetchTemplates();
      setShowEditor(false);
      setEditingTemplate(null);
      setSuccessMessage(`Email template ${isEditing ? 'updated' : 'created'} successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || error.message || 'Failed to save template');
    }
  };

  const handleSendEmail = async (sendData: any) => {
    try {
      setOriginalSendData(sendData); // Store the original send data
      const response = await apiClient.post('/music-email/send', sendData);

      if (response.status === 300) {
        // Handle conflicts
        setConflictData(response.data);
        setShowConflictModal(true);
        return;
      }

      // Log attachment details to console
      if (response.data?.details) {
        console.log('📧 Email sent successfully');
        console.log('✅ Attached files:', response.data.details.attached);
        console.log('⏭️ Skipped songs:', response.data.details.skipped);
        console.log('❌ Not found:', response.data.details.notFound);
      }

      setSuccessMessage('Email sent successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      if (error.response?.status === 300) {
        // Handle conflicts
        setConflictData(error.response.data);
        setShowConflictModal(true);
        return;
      }
      setError(error.response?.data?.error || error.message || 'Failed to send email');
    }
  };

  // Direct send without popup - uses template's pre-configured recipients
  const handleDirectSend = (template: EmailTemplate) => {
    // Check if template has recipients configured
    const recipients = template.recipient_emails;
    if (!recipients || (Array.isArray(recipients) && recipients.length === 0)) {
      setError(`No recipients configured for "${template.template_name}". Edit the template to add default recipients.`);
      return;
    }

    // Confirm before sending
    const recipientList = Array.isArray(recipients) ? recipients.join(', ') : recipients;
    confirm({
      title: 'Send Email',
      message: `Send email to: ${recipientList}?`,
      confirmText: 'Send',
      variant: 'info',
      onConfirm: async () => {
        setLoading(true);
        try {
          // Create form data for the send-with-attachments endpoint
          const formData = new FormData();
          formData.append('templateId', template.template_id.toString());
          formData.append('recipients', JSON.stringify(Array.isArray(recipients) ? recipients : [recipients]));
          formData.append('compilePDF', (template.compile_pdf ?? true).toString());
          formData.append('includeServiceMusic', (template.include_service_music ?? false).toString());
          formData.append('selectedFiles', JSON.stringify([])); // Let backend auto-find files

          const token = localStorage.getItem('token');
          const response = await fetch(`${apiClient.defaults.baseURL?.replace('/api', '')}/api/music-email/send-with-attachments`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: formData
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to send email');
          }

          const result = await response.json();
          console.log('📧 Email sent successfully', result);

          setSuccessMessage(`Email sent successfully to ${Array.isArray(recipients) ? recipients.length : 1} recipient(s)`);
          setTimeout(() => setSuccessMessage(null), 5000);
        } catch (error: any) {
          console.error('Error sending email:', error);
          setError(error.message || 'Failed to send email');
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleBatchEmail = async (batchData: any) => {
    try {
      const response = await apiClient.post('/music-email/send-batch', batchData);

      // Log attachment details to console for batch emails
      if (response.data?.details) {
        console.log('📧 Batch emails sent successfully');
        console.log('Details:', response.data.details);
      }

      setSuccessMessage('Batch emails sent successfully');
      setShowBatchModal(false);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      setError(error.response?.data?.error || error.message || 'Failed to send batch emails');
    }
  };

  const handleConflictResolution = async (resolutions: any[]) => {
    if (!selectedTemplate || !conflictData || !originalSendData) {
      console.error('Missing required data:', { selectedTemplate, conflictData, originalSendData });
      setError('Missing required data to resolve conflicts');
      return;
    }

    console.log('Handling conflict resolution with:', resolutions);

    try {
      const sendData = {
        ...originalSendData, // Use the original send data
        resolveConflicts: resolutions
      };

      console.log('Sending with resolved conflicts:', sendData);
      const response = await apiClient.post('/music-email/send', sendData);

      // Log attachment details to console
      if (response.data?.details) {
        console.log('📧 Email sent successfully with resolved conflicts');
        console.log('✅ Attached files:', response.data.details.attached);
        console.log('⏭️ Skipped songs:', response.data.details.skipped);
        console.log('❌ Not found:', response.data.details.notFound);
      }

      setSuccessMessage('Email sent successfully with resolved conflicts');
      setShowConflictModal(false);
      setConflictData(null);
      setOriginalSendData(null);
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (error: any) {
      console.error('Error sending email:', error);
      setError(error.response?.data?.error || error.message || 'Failed to send email');
    }
  };

  const getRecipientTypeBadge = (type: string) => {
    const colors = {
      admin: 'bg-primary',
      choir: 'bg-success', 
      band: 'bg-warning',
      custom: 'bg-info'
    };
    return colors[type as keyof typeof colors] || 'bg-secondary';
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container mt-4">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="container mt-4">
        <div className="d-flex align-items-center justify-content-between mb-4">
          <div className="d-flex align-items-center">
            <Mail className="me-2" size={32} />
            <h1>Email Templates</h1>
          </div>
          <button 
            className="btn btn-primary"
            onClick={() => {
              setEditingTemplate(null);
              setShowEditor(true);
            }}
          >
            <Plus size={20} className="me-2" />
            New Template
          </button>
          <button 
            className="btn btn-success ms-2"
            onClick={() => setShowBatchModal(true)}
            disabled={templates.length === 0}
          >
            <Users size={20} className="me-2" />
            Batch Send
          </button>
        </div>

        {/* Email Connection Status */}
        <div className="row mb-4">
          <div className="col-12">
            <div className={`alert ${emailConnectionStatus === 'connected' ? 'alert-success' : 'alert-warning'} d-flex align-items-center`}>
              {emailConnectionStatus === 'connected' ? (
                <CheckCircle size={20} className="me-2" />
              ) : (
                <AlertCircle size={20} className="me-2" />
              )}
              <span>
                Email Service: {emailConnectionStatus === 'connected' ? 'Connected' : 'Disconnected'}
                {emailConnectionStatus === 'disconnected' && (
                  <span className="ms-2">
                    <small>
                      {emailConfig ? (
                        <span>
                          Current backend configuration:
                          <code className="d-block mt-1">
                            SMTP_HOST={emailConfig.host}<br/>
                            SMTP_PORT={emailConfig.port}<br/>
                            SMTP_USER={emailConfig.user || '(not set)'}<br/>
                            SMTP_PASS={emailConfig.passwordConfigured ? '(configured)' : '(not set)'}
                          </code>
                        </span>
                      ) : (
                        <span>
                          Add SMTP settings to backend <code>.env</code> file:
                          <code className="d-block mt-1">
                            SMTP_HOST=smtp.gmail.com<br/>
                            SMTP_PORT=587<br/>
                            SMTP_USER=your-gmail@gmail.com<br/>
                            SMTP_PASS=your-gmail-app-password
                          </code>
                        </span>
                      )}
                    </small>
                  </span>
                )}
              </span>
              <button
                className="btn btn-sm btn-outline-secondary ms-auto"
                onClick={() => {
                  testEmailConnection();
                  fetchEmailConfig();
                }}
              >
                <TestTube size={16} className="me-1" />
                Test Connection
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {successMessage && (
          <div className="alert alert-success alert-dismissible" role="alert">
            {successMessage}
            <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)}></button>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible" role="alert">
            <strong>Setup Required:</strong> {error}
            {error.includes('database not set up') && (
              <div className="mt-2">
                <small>
                  <strong>Quick Fix:</strong>
                  <ol className="mb-0 mt-1">
                    <li>Open pgAdmin or your PostgreSQL client</li>
                    <li>Run the SQL script: <code>backend/create_email_templates_table.sql</code></li>
                    <li>Refresh this page</li>
                  </ol>
                </small>
              </div>
            )}
            {error.includes('server is not running') && (
              <div className="mt-2">
                <small>
                  <strong>Quick Fix:</strong> Run <code>npm run dev</code> in the <code>backend/</code> directory
                </small>
              </div>
            )}
            <button type="button" className="btn-close" onClick={() => setError(null)}></button>
          </div>
        )}

        {/* Templates List */}
        <div className="row">
          {templates.length === 0 ? (
            <div className="col-12">
              <div className="text-center py-5">
                <Mail size={64} className="text-muted mb-3" />
                <h3 className="text-muted">No Email Templates</h3>
                <p className="text-muted">Create your first email template to get started with automated music distribution</p>
                <button 
                  className="btn btn-primary"
                  onClick={() => {
                    setEditingTemplate(null);
                    setShowEditor(true);
                  }}
                >
                  <Plus size={20} className="me-2" />
                  Create Template
                </button>
              </div>
            </div>
          ) : (
            templates.map(template => (
              <div key={template.template_id} className="col-md-6 col-lg-4 mb-4">
                <div className="card h-100">
                  <div className="card-header d-flex align-items-center justify-content-between">
                    <div>
                      <h5 className="mb-1">{template.template_name}</h5>
                      <span className={`badge ${getRecipientTypeBadge(template.recipient_type)} small`}>
                        {template.recipient_type}
                      </span>
                    </div>
                    <div className="btn-group btn-group-sm">
                      <button
                        className="btn btn-outline-primary"
                        onClick={() => {
                          setEditingTemplate(template);
                          setShowEditor(true);
                        }}
                        title="Edit Template"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        onClick={() => handleDeleteTemplate(template.template_id)}
                        title="Delete Template"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="card-body">
                    <p className="card-text text-muted small mb-2">
                      <strong>Subject:</strong> {template.subject_template}
                    </p>
                    <p className="card-text text-muted small mb-2">
                      <strong>Weeks Ahead:</strong> {template.weeks_ahead}
                    </p>
                    <p className="card-text text-muted small mb-2">
                      <strong>Music Folders:</strong> {template.music_folder_paths.length > 0 ? `${template.music_folder_paths.length} folder(s)` : 'Not configured'}
                    </p>
                    <div className="mt-3">
                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => handleDirectSend(template)}
                        disabled={loading}
                      >
                        <Send size={16} className="me-1" />
                        {loading ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Modals */}
        {showEditor && (
          <SimpleEmailEditor
            template={editingTemplate}
            onSave={handleSaveTemplate}
            onCancel={() => {
              setShowEditor(false);
              setEditingTemplate(null);
            }}
          />
        )}

        {showConflictModal && conflictData && (
          <FileConflictModal
            conflicts={conflictData.conflicts}
            onResolve={handleConflictResolution}
            onCancel={() => {
              setShowConflictModal(false);
              setConflictData(null);
            }}
          />
        )}

        {showBatchModal && (
          <BatchEmailModal
            templates={templates}
            onSend={handleBatchEmail}
            onCancel={() => setShowBatchModal(false)}
          />
        )}

        <ConfirmDeleteModal />
      </div>
    </>
  );
};

export default EmailTemplatesPage;